import { IconExternalLink } from '@tabler/icons-react';
import { Button, Checkbox, Grid, Group, Modal, SegmentedControl, Switch, Text, TextInput } from '@mantine/core';
import LabeledSlider from '../LabeledSlider';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';
import useFetch from '../../helpers/useFetch';
import ErrorMessage from '../ErrorMessage';
import OptionsSelect from '../OptionsSelect';
import SegmentSelector from '../Segments/SegmentSelector';
import Spinner from '../Spinner';

const SyncSegmentsForm = ({ segmentSync }) => {
  const fetch = useFetch();
  const { accountId, domainId, segmentSyncId } = useParams();
  const [form, setForm] = useState(segmentSync);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();
  const [segments, setSegments] = useState();
  const [segmentsLoading, setSegmentsLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [terms, setTerms] = useState({ accepted: false, valueBasedAccepted: false });
  const [termsLoading, setTermsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const facebook = useSelector((state) => state.facebook);
  const [toggle, setToggle] = useState(!!segmentSync?.adAccountId);

  // Determine if this is an edit operation
  const isEdit = !!segmentSyncId;

  useEffect(() => {
    setSegmentsLoading(true);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/segments`)
      .then(({ segments }) => setSegments(segments))
      .catch((err) => {
        setError(err.message || 'Failed to load segments');
      })
      .finally(() => {
        setSegmentsLoading(false);
      });
  }, [fetch, accountId, domainId]);

  useEffect(() => {
    setTerms({ accepted: false, valueBasedAccepted: false });

    const adAccountIdToCheck = toggle ? form?.adAccountId : facebook?.user?.adAccountId;

    if (adAccountIdToCheck && form?.type === 'facebook') {
      setTermsLoading(true);
      fetch(
        `/api/admin/accounts/${accountId}/domains/${domainId}/facebook/customaudiences/tos?adAccountId=${adAccountIdToCheck}`
      )
        .then(({ accepted, valueBasedAccepted }) => {
          setTerms({ accepted, valueBasedAccepted });
        })
        .catch(setError)
        .finally(() => setTermsLoading(false));
    }
  }, [fetch, accountId, domainId, form?.adAccountId, form?.type, toggle, facebook?.user?.adAccountId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const setType = (type) => {
    setForm((prev) => ({
      ...prev,
      type,
      // Clear segmentId when type changes to avoid invalid segment selection
      segmentId: null,
      // Clear lookalike fields when type is not Facebook
      includeLookalikes: type === 'facebook' ? prev.includeLookalikes : false,
      lookalikeType: type === 'facebook' ? prev.lookalikeType : null,
    }));
  };

  const setSegment = (segment) => {
    setForm((prev) => ({ ...prev, segmentId: segment?.id || null }));
  };

  const setLookalikeType = (lookalikeType) => {
    setForm((prev) => ({
      ...prev,
      lookalikeType,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const requestBody = {
        name: form.name,
        type: form.type,
        segmentId: form.segmentId,
        includeLookalikes: form.includeLookalikes || false,
        lookalikeType: form.lookalikeType || null,
        adAccountId: form.adAccountId,
        ratio: form.ratio || null,
      };

      let response;
      if (isEdit) {
        response = await fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/segmentSyncs/${segmentSyncId}`, {
          method: 'PUT',
          body: JSON.stringify(requestBody),
        });
      } else {
        response = await fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/segmentSyncs`, {
          method: 'POST',
          body: JSON.stringify(requestBody),
        });
      }

      if (response.segmentSync) {
        if (isEdit) {
          navigate(location.pathname.replace(`/syncSegments/${segmentSyncId}/details`, '/syncSegments'));
        } else {
          navigate(location.pathname.replace(`/syncSegments/new`, '/syncSegments'));
        }
      }
    } catch (err) {
      setError(err.message || `Failed to ${isEdit ? 'update' : 'create'} segment sync`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    setError(null);

    try {
      await fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/segmentSyncs/${segmentSyncId}`, {
        method: 'DELETE',
      });

      // Navigate back to the sync segments list
      navigate(location.pathname.replace(`/syncSegments/${segmentSyncId}/details`, '/syncSegments'));
    } catch (err) {
      setError(err.message || 'Failed to delete segment sync');
    } finally {
      setDeleteLoading(false);
      setShowDeleteModal(false);
    }
  };

  const scopes = ['behavior'];
  const filteredSegments = segments?.filter((segment) => scopes.includes(segment.scope));

  if (segmentsLoading) {
    return <Spinner />;
  }

  return (
    <form onSubmit={handleSubmit}>
      {error && <ErrorMessage message={error} />}

      <Grid align="center" mb="sm">
        <Grid.Col span={{ base: 12, sm: 2 }}>
          <Text fw={500}>Name</Text>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 10 }}>
          <TextInput
            name="name"
            value={form.name || ''}
            disabled={isEdit}
            onChange={handleChange}
            required
          />
        </Grid.Col>
      </Grid>
      <Grid align="center" mb="sm">
        <Grid.Col span={{ base: 12, sm: 2 }}>
          <Text fw={500}>Type</Text>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 10 }}>
          <SegmentedControl
            disabled={isEdit}
            value={form.type ?? ''}
            onChange={setType}
            data={[
              { value: 'facebook', label: 'Facebook' },
              { value: 'google', label: 'Google' },
            ]}
            aria-label="Sync Type"
          />
        </Grid.Col>
      </Grid>

      {form.type && (
        <SegmentSelector
          segmentId={form.segmentId}
          onSelect={setSegment}
          loadedSegments={filteredSegments}
          title="Segment"
          description={`Select a segment to sync to ${
            form.type === 'facebook' ? 'Facebook' : 'Google'
          }. Only segments with scope 'behavior' are available.`}
          dataTour="segmentSelector"
          sizeTitle={2}
          sizeBody={10}
          disabled={isEdit}
          disabledScopes={['facebook', 'google', 'profileFilter']}
        />
      )}

      {form.type === 'facebook' && (
        <>
          <Grid align="center" mb="sm">
            <Grid.Col span={{ base: 12, sm: 2 }}>
              <Text fw={500}>Sync to another Facebook Ad account</Text>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 10 }}>
              <Switch
                defaultChecked={toggle}
                onChange={(e) => {
                  setToggle(e.target.checked);
                }}
                disabled={isEdit}
              />
              <Text size="sm" c="dimmed" mt="xs">
                Choose different Facebook Ad account from the one configured in the Settings/Facebook and Whatsapp where
                the audience will be created.
              </Text>
            </Grid.Col>
          </Grid>
          {!toggle && form.type === 'facebook' && facebook?.user?.adAccountId && !termsLoading && (!terms.accepted || !terms.valueBasedAccepted) && (
            <Grid align="flex-start" mb="sm">
              <Grid.Col span={{ base: 12, sm: 2 }} />
              <Grid.Col span={{ base: 12, sm: 10 }}>
                {!terms.accepted && (
                  <Text size="sm" c="red" mb="xs">
                    You need to accept Facebook Custom Audience Terms of Service{' '}
                    <a
                      href={`https://business.facebook.com/ads/manage/customaudiences/tos/?act=${facebook.user.adAccountId.replace(
                        'act_',
                        ''
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {' '}
                      <IconExternalLink size={14} /> in the Business Manager
                    </a>
                    . <br />
                  </Text>
                )}
                {!terms.valueBasedAccepted && (
                  <Text size="sm" c="red" mb="xs">
                    You need to accept Facebook Value Based Custom Audience Terms of Service{' '}
                    <a
                      href={`https://www.facebook.com/customaudiences/value_based/tos/?act=${facebook.user.adAccountId.replace(
                        'act_',
                        ''
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {' '}
                      <IconExternalLink size={14} /> in the Business Manager
                    </a>
                    . <br />
                  </Text>
                )}
              </Grid.Col>
            </Grid>
          )}
          {toggle && (
            <Grid align="flex-start" mb="sm">
              <Grid.Col span={{ base: 12, sm: 2 }}>
                <Text fw={500}>Ad account</Text>
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 10 }}>
                {form?.adAccountId && !termsLoading && !terms.accepted && (
                  <Text size="sm" c="red" mb="xs">
                    You need to accept Facebook Custom Audience Terms of Service{' '}
                    <a
                      href={`https://business.facebook.com/ads/manage/customaudiences/tos/?act=${form.adAccountId.replace(
                        'act_',
                        ''
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {' '}
                      <IconExternalLink size={14} /> in the Business Manager
                    </a>
                    . <br />
                  </Text>
                )}
                {form?.adAccountId && !termsLoading && !terms.valueBasedAccepted && (
                  <Text size="sm" c="red" mb="xs">
                    You need to accept Facebook Value Based Custom Audience Terms of Service{' '}
                    <a
                      href={`https://www.facebook.com/customaudiences/value_based/tos/?act=${form.adAccountId.replace(
                        'act_',
                        ''
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {' '}
                      <IconExternalLink size={14} /> in the Business Manager
                    </a>
                    . <br />
                  </Text>
                )}

                <OptionsSelect
                  name="adAccount"
                  endpoint={`facebook/adAccounts/${facebook.user?.businessId}`}
                  handleChange={handleChange}
                  text="The created Facebook audience will be associated with the selected Ad account."
                  value={form?.adAccountId || ''}
                  required={true}
                  disabled={isEdit}
                />
              </Grid.Col>
            </Grid>
          )}
          <Grid align="center" mb="sm">
            <Grid.Col span={{ base: 12, sm: 2 }}>
              <Text fw={500}>Create Lookalike Audience</Text>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 10 }}>
              <Checkbox
                name="includeLookalikes"
                checked={form.includeLookalikes || false}
                onChange={(e) => handleChange({ target: { name: 'includeLookalikes', type: 'checkbox', checked: e.target.checked } })}
                label="Enable lookalike audience creation"
                disabled={segmentSync?.includeLookalikes}
              />
            </Grid.Col>
          </Grid>
          {form.includeLookalikes && (
            <Grid align="flex-start" mb="sm">
              <Grid.Col span={{ base: 12, sm: 2 }}>
                <Text fw={500}>Lookalike Type</Text>
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 10 }}>
                <SegmentedControl
                  value={form.lookalikeType ?? ''}
                  onChange={setLookalikeType}
                  data={[
                    { value: 'reach', label: 'Reach' },
                    { value: 'similarity', label: 'Similarity' },
                    { value: 'custom_ratio', label: 'Custom ratio' },
                  ]}
                  aria-label="Lookalike Type"
                  disabled={isEdit}
                />
                <Text size="sm" c="dimmed" mt="xs">
                  <strong>Reach:</strong> Includes a larger number of profiles which are somewhat similar to your source
                  audience.
                  <br />
                  <strong>Similarity:</strong> Includes a smaller number of profiles that are very similar to your
                  source audience.
                  <strong>Custom ratio:</strong> Choose the top 1%-20% of people most similar to your source audience.
                  Lower percentages give you higher precision with smaller reach, higher percentages give you broader
                  reach with less precision.
                </Text>
              </Grid.Col>
            </Grid>
          )}
          {form.lookalikeType === 'custom_ratio' && (
            <Grid align="center" mb="sm">
              <Grid.Col span={{ base: 12, sm: 2 }}>
                <Text fw={500}>Ratio %</Text>
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 10 }}>
                <LabeledSlider
                  min={1}
                  max={20}
                  step={1}
                  value={form?.ratio || 3}
                  onChange={(value) => handleChange({ target: { name: 'ratio', value } })}
                  disabled={isEdit}
                />
              </Grid.Col>
            </Grid>
          )}
        </>
      )}

      <Group justify="flex-end" mt="md">
        <Button variant="default" onClick={() => navigate(-1)}>
          Back
        </Button>
        {isEdit && (
          <Button color="red" onClick={() => setShowDeleteModal(true)} disabled={deleteLoading}>
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        )}
        <Button
          type="submit"
          disabled={
            loading ||
            (form.type === 'facebook' && toggle && (!form.adAccountId || !form.segmentId)) ||
            (form.type === 'facebook' &&
              (toggle ? !!form.adAccountId : !!facebook?.user?.adAccountId) &&
              !termsLoading &&
              (!terms.accepted || !terms.valueBasedAccepted))
          }
        >
          {loading ? (isEdit ? 'Updating...' : 'Creating...') : isEdit ? 'Update' : 'Create'}
        </Button>
      </Group>

      {/* Delete Confirmation Modal */}
      <Modal opened={showDeleteModal} onClose={() => setShowDeleteModal(false)} title="Confirm Delete">
        <Text>
          Are you sure you want to delete the segment sync "{form.name}"? This action cannot be undone.
        </Text>
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button color="red" onClick={handleDelete} disabled={deleteLoading}>
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </Group>
      </Modal>
    </form>
  );
};

export default SyncSegmentsForm;
